import React, { useState, useCallback, useMemo } from 'react';
import { Paper } from '@mui/material';
import { InspectionStandard } from '@Types';
import { AssetTypeId, SystemTypeId } from '@Types/assetSchema.types';
import {
	AssetDetailActions,
	InspDetailActions,
} from '@Components/RehabResult/RehabResult.component';
import { AssetDetails, InspDetails } from '@innovyze/lib-am-common/Components';
import { useGlobalization } from '@Translations';

export interface UseDetailPanelArgs {
	hasFullAccess: boolean;
	currentTreeSystemType: SystemTypeId | undefined;
}

export interface UseDetailPanelResult {
	assetDetailActions: AssetDetailActions;
	showDetailInspection: () => string | false;
	showDetailAsset: () => boolean | undefined;
	detInsp: React.ReactNode;
	detAss: React.ReactNode;
}

export const useDetailPanel = ({
	hasFullAccess,
	currentTreeSystemType,
}: UseDetailPanelArgs): UseDetailPanelResult => {
	const { t } = useGlobalization();

	const [detailInspection, setDetailInspection] = useState<string[]>([
		'', // Video URL
		'', // Inspection ID
		'', // Standard
	]);
	const [detailAsset, setDetailAsset] = useState<
		| { assetId: string; systemType: SystemTypeId; assetType: AssetTypeId }
		| undefined
	>(undefined);

	const onClickAway = useCallback(() => {
		setDetailInspection(['', '', '']);
		setDetailAsset(undefined);
	}, []);

	const onInspDetailClicked = useCallback(
		(
			videoURL: string,
			inspectionID: string,
			standard: InspectionStandard,
		) => {
			setDetailInspection([videoURL, inspectionID, standard.toString()]);
		},
		[],
	);

	const inspDetailActions: InspDetailActions = useMemo(
		() => ({
			onInspectionDetailClicked: onInspDetailClicked,
		}),
		[onInspDetailClicked],
	);

	const onAssetDetailClicked = useCallback(
		(assetId: string, systemType: SystemTypeId, assetType: AssetTypeId) => {
			setDetailAsset({ assetId, systemType, assetType });
		},
		[],
	);

	const assetDetailActions: AssetDetailActions = useMemo(
		() => ({
			onAssetDetailClicked: onAssetDetailClicked,
		}),
		[onAssetDetailClicked],
	);

	const showDetailInspection = () => {
		return detailInspection?.length == 3 && detailInspection[1];
	};

	const showDetailAsset = () => {
		return detailAsset && !showDetailInspection();
	};

	const detInsp = useMemo(
		() =>
			showDetailInspection() ? (
				<Paper
					style={{
						width: '100%',
						height: '100%',
						top: '0%',
						left: '0%',
						position: 'relative',
					}}>
					<InspDetails
						back={{
							action: () => {
								setDetailInspection(['', '', '']);
							},
							label: t('Back to Asset Details'),
						}}
						inspectionId={detailInspection[1]}
						standard={detailInspection[2] as InspectionStandard}
					/>
				</Paper>
			) : null,
		[detailInspection],
	);

	const detAss = useMemo(
		() =>
			showDetailAsset() ? (
				<Paper
					style={{
						width: '100%',
						height: '100%',
						top: '0%',
						left: '0%',
						position: 'relative',
					}}>
					<AssetDetails
						assetId={detailAsset?.assetId}
						back={{
							action: onClickAway,
							label: t('Back to Rehab Results'),
						}}
						assetType={detailAsset?.assetType.id}
						actions={inspDetailActions}
						adminFullAccess={hasFullAccess}
						systemType={currentTreeSystemType?.id}
					/>
				</Paper>
			) : null,
		[detailAsset, detailInspection],
	);

	return {
		assetDetailActions,
		showDetailInspection,
		showDetailAsset,
		detInsp,
		detAss,
	};
};
